import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  FlaskConical, Leaf, FileText, Package, 
  Plus, Search, Filter, Beaker, 
  ClipboardList, AlertTriangle, CheckCircle,
  MessageSquare, Send, Bot, Sparkles,
  TestTube, Scale, ShieldCheck, Settings
} from 'lucide-react';

export default function AgentCasiaPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('formulations');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [formulations, setFormulations] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [essais, setEssais] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Dialog states
  const [showNewFormulation, setShowNewFormulation] = useState(false);
  const [showNewMatiere, setShowNewMatiere] = useState(false);
  
  // Form states
  const [newFormulation, setNewFormulation] = useState({
    nom: '',
    code: '',
    famille: 'cosmetique',
    description: ''
  });
  
  const [newMatiere, setNewMatiere] = useState({
    nom: '',
    code: '',
    type_matiere: 'huile_essentielle',
    famille_olfactive: '',
    origine: '',
    fournisseur: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, formRes, matRes, essaisRes, cmdRes] = await Promise.all([
        api.get('/casia-agent/stats').catch(() => ({ data: null })),
        api.get('/casia-agent/formulations').catch(() => ({ data: [] })),
        api.get('/casia-agent/matieres').catch(() => ({ data: [] })),
        api.get('/casia-agent/essais').catch(() => ({ data: [] })),
        api.get('/casia-agent/commandes').catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setFormulations(Array.isArray(formRes.data) ? formRes.data : []);
      setMatieres(Array.isArray(matRes.data) ? matRes.data : []);
      setEssais(Array.isArray(essaisRes.data) ? essaisRes.data : []);
      setCommandes(Array.isArray(cmdRes.data) ? cmdRes.data : []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await api.post('/casia-agent/chat', { message: chatInput });
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error("Impossible de communiquer avec l'agent CASIA");
    } finally {
      setChatLoading(false);
    }
  };

  const createFormulation = async () => {
    try {
      await api.post('/casia-agent/formulations', newFormulation);
      toast.success("Formulation créée");
      setShowNewFormulation(false);
      setNewFormulation({ nom: '', code: '', famille: 'cosmetique', description: '' });
      loadData();
    } catch (error) {
      toast.error("Impossible de créer la formulation");
    }
  };

  const createMatiere = async () => {
    try {
      await api.post('/casia-agent/matieres', newMatiere);
      toast.success("Matière première ajoutée");
      setShowNewMatiere(false);
      setNewMatiere({ nom: '', code: '', type_matiere: 'huile_essentielle', famille_olfactive: '', origine: '', fournisseur: '' });
      loadData();
    } catch (error) {
      toast.error("Impossible d'ajouter la matière");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      brouillon: 'bg-gray-500',
      en_test: 'bg-yellow-500',
      valide: 'bg-green-500',
      production: 'bg-blue-500',
      archive: 'bg-slate-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getTypeIcon = (type) => {
    const icons = {
      huile_essentielle: <FlaskConical className="h-4 w-4" />,
      hydrolat: <Beaker className="h-4 w-4" />,
      huile_vegetale: <Leaf className="h-4 w-4" />,
      extrait: <TestTube className="h-4 w-4" />
    };
    return icons[type] || <Package className="h-4 w-4" />;
  };

  return (
    <div className="p-6 space-y-6" data-testid="agent-casia-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            Agent CASIA
          </h1>
          <p className="text-muted-foreground mt-1">R&D, Formulation & Conformité réglementaire</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/agents/config', { state: { selectedAgent: 'technique' } })}
            data-testid="agent-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>
          <Button variant="outline" onClick={() => setShowNewMatiere(true)}>
            <Leaf className="h-4 w-4 mr-2" />
            Nouvelle Matière
          </Button>
          <Button onClick={() => setShowNewFormulation(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Formulation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Formulations</p>
                <p className="text-2xl font-bold">{stats?.formulations || formulations.length}</p>
              </div>
              <FlaskConical className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Matières</p>
                <p className="text-2xl font-bold">{stats?.matieres || matieres.length}</p>
              </div>
              <Leaf className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Essais en cours</p>
                <p className="text-2xl font-bold">{stats?.essais_en_cours || 0}</p>
              </div>
              <TestTube className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Commandes</p>
                <p className="text-2xl font-bold">{stats?.commandes_prep || commandes.length}</p>
              </div>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Conformité</p>
                <p className="text-2xl font-bold text-green-500">98%</p>
              </div>
              <ShieldCheck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Tabs Content */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="formulations">Formulations</TabsTrigger>
              <TabsTrigger value="matieres">Matières</TabsTrigger>
              <TabsTrigger value="essais">Essais</TabsTrigger>
              <TabsTrigger value="commandes">Commandes</TabsTrigger>
            </TabsList>

            <TabsContent value="formulations" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Rechercher une formulation..." 
                    className="pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Button variant="outline" size="icon">
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
              
              {formulations.length === 0 ? (
                <Card className="p-8 text-center">
                  <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune formulation. Créez votre première formulation !</p>
                  <Button className="mt-4" onClick={() => setShowNewFormulation(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une formulation
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {formulations.filter(f => 
                    f.nom?.toLowerCase().includes(search.toLowerCase()) ||
                    f.code?.toLowerCase().includes(search.toLowerCase())
                  ).map((form) => (
                    <Card key={form.id} className="hover:shadow-md transition-shadow cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{form.nom}</h3>
                              <Badge variant="outline">{form.code}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge>{form.famille}</Badge>
                              <Badge className={getStatusColor(form.statut)}>{form.statut}</Badge>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">Voir</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="matieres" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Rechercher une matière..." className="pl-10" />
                </div>
              </div>
              
              {matieres.length === 0 ? (
                <Card className="p-8 text-center">
                  <Leaf className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune matière première enregistrée.</p>
                  <Button className="mt-4" onClick={() => setShowNewMatiere(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une matière
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {matieres.map((mat) => (
                    <Card key={mat.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            {getTypeIcon(mat.type_matiere)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium">{mat.nom}</h4>
                            <p className="text-xs text-muted-foreground">{mat.code}</p>
                            <div className="flex gap-1 mt-2">
                              <Badge variant="outline" className="text-xs">{mat.type_matiere?.replace('_', ' ')}</Badge>
                              {mat.origine && <Badge variant="secondary" className="text-xs">{mat.origine}</Badge>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="essais" className="space-y-4 mt-4">
              {essais.length === 0 ? (
                <Card className="p-8 text-center">
                  <TestTube className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun essai en cours.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {essais.map((essai) => (
                    <Card key={essai.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between">
                          <div>
                            <h4 className="font-medium">{essai.nom}</h4>
                            <p className="text-sm text-muted-foreground">{essai.type_essai}</p>
                          </div>
                          <Badge>{essai.statut}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="commandes" className="space-y-4 mt-4">
              {commandes.length === 0 ? (
                <Card className="p-8 text-center">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune commande en préparation.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {commandes.map((cmd) => (
                    <Card key={cmd.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between">
                          <div>
                            <h4 className="font-medium">{cmd.reference}</h4>
                            <p className="text-sm text-muted-foreground">{cmd.client_nom}</p>
                          </div>
                          <Badge>{cmd.statut}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: Chat Assistant */}
        <Card className="h-[600px] flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bot className="h-5 w-5 text-emerald-500" />
              Assistant CASIA
            </CardTitle>
            <CardDescription>Posez vos questions sur les formulations et la conformité</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-2 bg-muted/30 rounded-lg">
              {chatMessages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Demandez-moi de l'aide sur vos formulations, la conformité REACH, ou les bonnes pratiques...</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-background border'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-background border p-3 rounded-lg">
                    <div className="flex gap-1">
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.1s' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input 
                placeholder="Posez votre question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleChat()}
              />
              <Button onClick={handleChat} disabled={chatLoading}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog: Nouvelle Formulation */}
      <Dialog open={showNewFormulation} onOpenChange={setShowNewFormulation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Formulation</DialogTitle>
            <DialogDescription>Créez une nouvelle formulation produit</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input 
                  value={newFormulation.nom}
                  onChange={(e) => setNewFormulation({...newFormulation, nom: e.target.value})}
                  placeholder="Sérum visage anti-âge"
                />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input 
                  value={newFormulation.code}
                  onChange={(e) => setNewFormulation({...newFormulation, code: e.target.value})}
                  placeholder="FORM-001"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Famille</Label>
              <Select 
                value={newFormulation.famille}
                onValueChange={(v) => setNewFormulation({...newFormulation, famille: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cosmetique">Cosmétique</SelectItem>
                  <SelectItem value="phytosanitaire">Phytosanitaire</SelectItem>
                  <SelectItem value="bien_etre">Bien-être</SelectItem>
                  <SelectItem value="alimentaire">Alimentaire</SelectItem>
                  <SelectItem value="veterinaire">Vétérinaire</SelectItem>
                  <SelectItem value="nettoyant">Nettoyant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={newFormulation.description}
                onChange={(e) => setNewFormulation({...newFormulation, description: e.target.value})}
                placeholder="Description du produit et de ses propriétés..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFormulation(false)}>Annuler</Button>
            <Button onClick={createFormulation}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nouvelle Matière */}
      <Dialog open={showNewMatiere} onOpenChange={setShowNewMatiere}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Matière Première</DialogTitle>
            <DialogDescription>Ajoutez une matière à votre base de données</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input 
                  value={newMatiere.nom}
                  onChange={(e) => setNewMatiere({...newMatiere, nom: e.target.value})}
                  placeholder="Lavande vraie"
                />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input 
                  value={newMatiere.code}
                  onChange={(e) => setNewMatiere({...newMatiere, code: e.target.value})}
                  placeholder="HE-LAV-001"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newMatiere.type_matiere}
                  onValueChange={(v) => setNewMatiere({...newMatiere, type_matiere: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="huile_essentielle">Huile essentielle</SelectItem>
                    <SelectItem value="hydrolat">Hydrolat</SelectItem>
                    <SelectItem value="huile_vegetale">Huile végétale</SelectItem>
                    <SelectItem value="extrait">Extrait</SelectItem>
                    <SelectItem value="poudre">Poudre</SelectItem>
                    <SelectItem value="cire">Cire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Origine</Label>
                <Input 
                  value={newMatiere.origine}
                  onChange={(e) => setNewMatiere({...newMatiere, origine: e.target.value})}
                  placeholder="France - Provence"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input 
                value={newMatiere.fournisseur}
                onChange={(e) => setNewMatiere({...newMatiere, fournisseur: e.target.value})}
                placeholder="Production interne"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMatiere(false)}>Annuler</Button>
            <Button onClick={createMatiere}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
